import ApiService from '../api'

class ReportServices {
  /**
   * Handle different API response formats consistently
   */
  static normalizeResponse(response) {
    // Handle nested Laravel structure: { Data: { success: true, data: {...} } }
    if (response?.Data?.success && response?.Data?.data) {
      return {
        success: true,
        data: response.Data.data
      }
    }

    // Handle direct structure: { success: true, data: {...} }
    if (response?.success && response?.data) {
      return response
    }

    // Handle case where response.data exists but no success flag
    if (response?.data) {
      return {
        success: true,
        data: response.data
      }
    }

    // Handle case where response IS the data (no wrapper)
    if (response && typeof response === 'object' && !response.success && !response.data) {
      return {
        success: true,
        data: response
      }
    }

    console.warn('Unexpected response format:', response)
    throw new Error('Invalid response format')
  }

  /**
   * Get dashboard metrics
   */
  static async getDashboardMetrics() {
    try {
      const response = await ApiService.get('/reports')

      return this.normalizeResponse(response)
    } catch (error) {
      console.error('Failed to fetch dashboard metrics:', error)

      // Return fallback data structure that matches the expected format
      return {
        success: true,
        data: {
          metrics: {
            total_fellowships: 0,
            total_publications: 0,
            total_grants: 0,
            total_funding: 0,
            students_supervised: 0,
            total_fellows: 0,
            recent_fellows: 0,
            recent_fellowships: 0,
            fellow_data_completeness: 0,
            fellowship_data_completeness: 0
          }
        }
      }
    }
  }

  /**
   * Get fellowship statistics
   */
  static async getFellowshipStats() {
    try {
      const response = await ApiService.get('/reports/fellowship-stats')
      return this.normalizeResponse(response)
    } catch (error) {
      console.error('Failed to fetch fellowship stats:', error)

      // Return fallback data
      return {
        success: true,
        data: {
          fellowships_per_year: {},
          fellowships_per_call: [],
          fellowships_per_country: { all: {} },
          fellowships_per_disease_area: [],
          gender_distribution: [],
          completion_stats: {
            total: 0,
            completed: 0,
            active: 0,
            pending: 0,
            completion_rate: 0
          }
        }
      }
    }
  }

  /**
   * Get publication statistics
   */
  static async getPublicationStats() {
    try {
      const response = await ApiService.get('/reports/publication-stats')

      return this.normalizeResponse(response)
    } catch (error) {
      console.error('Failed to fetch publication stats:', error)

      return {
        success: true,
        data: {
          publications_per_year: [],
          publications_by_fellowship: [],
          average_publications: 0,
          total_publications: 0,
          current_year: 0
        }
      }
    }
  }

  /**
   * Get grant statistics
   */
  static async getGrantStats() {
    try {
      const response = await ApiService.get('/reports/grant-stats')

      return this.normalizeResponse(response)
    } catch (error) {
      console.error('Failed to fetch grant stats:', error)

      return {
        success: true,
        data: {
          grants_per_year: [],
          grants_per_role: [],
          funding_per_year: {},
          funding_per_source: {}
        }
      }
    }
  }

  /**
   * Get project statistics
   */
  static async getProjectStats() {
    try {
      const response = await ApiService.get('/reports/project-stats')

      return this.normalizeResponse(response)
    } catch (error) {
      console.error('Failed to fetch project stats:', error)

      return {
        success: true,
        data: {
          projects_per_year: [],
          students_supervised: { total: 0, by_year: {} }
        }
      }
    }
  }

  /**
   * Export data to Excel
   */
  static async exportToExcel() {
    try {
      // Request blob from API
      const response = await ApiService.post('/reports/export-excel', {
        responseType: 'blob'
      });

      // If API returned a raw fetch() response
      if (response && response instanceof Response) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.setAttribute(
          'download',
          `fellowship-reports-${new Date().toISOString().split('T')[0]}.xlsx`
        );

        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        return { success: true };
      }

      console.error("❌ Export failed: Not a blob response", response);
      return { success: false };

    } catch (error) {
      console.error('Failed to export to Excel:', error);
      throw error;
    }
  }

  /**
   * Get fellowship details for specific fellow
   */
  static async getFellowshipDetails(fellowId) {
    try {
      const response = await ApiService.get(`/reports/fellows/${fellowId}`)
      return this.normalizeResponse(response)
    } catch (error) {
      console.error('Failed to fetch fellowship details:', error)
      throw error
    }
  }

  /**
   * Get geographic distribution data
   */
  static async getGeographicData() {
    try {
      const response = await ApiService.get('/reports/geographic')
      return this.normalizeResponse(response)
    } catch (error) {
      console.error('Failed to fetch geographic data:', error)
      throw error
    }
  }

  /**
   * Get filtered reports based on criteria
   */
  static async getFilteredReports(filters = {}) {
    try {
      const params = new URLSearchParams()

      Object.keys(filters).forEach(key => {
        if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
          params.append(key, filters[key])
        }
      })

      const response = await ApiService.get(`/reports/filtered?${params.toString()}`)
      return this.normalizeResponse(response)
    } catch (error) {
      console.error('Failed to fetch filtered reports:', error)
      throw error
    }
  }

  /**
   * Get real-time metrics
   */
  static async getRealtimeMetrics() {
    try {
      const response = await ApiService.get('/reports/realtime/metrics')
      return this.normalizeResponse(response)
    } catch (error) {
      console.error('Failed to fetch real-time metrics:', error)
      throw error
    }
  }

  /**
   * Get recent activities
   */
  static async getRecentActivities() {
    try {
      const response = await ApiService.get('/reports/realtime/activities')
      return this.normalizeResponse(response)
    } catch (error) {
      console.error('Failed to fetch recent activities:', error)
      throw error
    }
  }

  /**
   * Get notification counts
   */
  static async getNotificationCounts() {
    try {
      const response = await ApiService.get('/reports/realtime/notifications')
      return this.normalizeResponse(response)
    } catch (error) {
      console.error('Failed to fetch notification counts:', error)
      throw error
    }
  }

  /**
   * Test API connectivity
   */
  static async testConnection() {
    try {
      const response = await ApiService.get('/reports-utils/health')
      return this.normalizeResponse(response)
    } catch (error) {
      console.error('API health check failed:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Get lookup tables for dropdown values
   */
  static async getLookupTables() {
    try {
      const response = await ApiService.get('/reports-utils/lookups')
      return this.normalizeResponse(response)
    } catch (error) {
      console.error('Failed to fetch lookup tables:', error)
      throw error
    }
  }

  /**
   * Validate report parameters
   */
  static async validateReportParams(params) {
    try {
      const response = await ApiService.post('/reports-utils/validate-params', params)
      return this.normalizeResponse(response)
    } catch (error) {
      console.error('Failed to validate report parameters:', error)
      throw error
    }
  }

  /**
   * Get report metadata
   */
  static async getReportMetadata(reportType) {
    try {
      const response = await ApiService.get(`/reports-utils/metadata/${reportType}`)
      return this.normalizeResponse(response)
    } catch (error) {
      console.error('Failed to fetch report metadata:', error)
      throw error
    }
  }

  /**
   * Batch data loading with error handling
   */
  static async loadAllReportData() {
    const results = {}

    try {
      // Load all data sources in parallel with individual error handling
      const promises = [
        this.getDashboardMetrics().catch(err => ({ error: err, type: 'metrics' })),
        this.getFellowshipStats().catch(err => ({ error: err, type: 'fellowships' })),
        this.getPublicationStats().catch(err => ({ error: err, type: 'publications' })),
        this.getGrantStats().catch(err => ({ error: err, type: 'grants' })),
        this.getProjectStats().catch(err => ({ error: err, type: 'projects' }))
      ]

      const responses = await Promise.allSettled(promises)

      responses.forEach((result, index) => {
        const types = ['metrics', 'fellowships', 'publications', 'grants', 'projects']
        const type = types[index]

        if (result.status === 'fulfilled' && !result.value.error) {
          results[type] = result.value
        } else {
          console.warn(`Failed to load ${type}:`, result.reason || result.value.error)
          results[type] = { success: false, error: result.reason || result.value.error }
        }
      })

      return {
        success: true,
        data: results,
        errors: Object.keys(results).filter(key => !results[key].success)
      }
    } catch (error) {
      console.error('Batch data loading failed:', error)
      return {
        success: false,
        error: error.message,
        data: results
      }
    }
  }

  /**
   * Debug method to test different endpoints
   */
  static async debugEndpoints() {
    const endpoints = [
      '/reports',
      '/reports/fellowship-stats',
      '/reports/publication-stats',
      '/reports/grant-stats',
      '/reports-utils/health'
    ]

    const results = {}

    for (const endpoint of endpoints) {
      try {
        const response = await ApiService.get(endpoint)
        results[endpoint] = {
          success: true,
          data: response,
          status: 'OK'
        }
      } catch (error) {
        results[endpoint] = {
          success: false,
          error: error.message,
          status: error.response?.status || 'NETWORK_ERROR'
        }
        console.error(`✗ ${endpoint}:`, error)
      }
    }

    return results
  }
}

export default ReportServices
